import React, { useEffect, useRef } from 'react';

// iPads have no number-only keyboard (inputmode=numeric opens the full keyboard), so number
// fields pop up this on-page pad instead. iPadOS reports itself as a Mac with touch.
export const needsPagePad = () => typeof navigator !== 'undefined'
  && (/iPad/.test(navigator.userAgent || '') || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

export const padKey = (value, key) => {
  if (key === 'back') return value.slice(0, -1);
  return `${value}${key}`.replace(/^0+/, '').slice(0, 6);
};

const PAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'back', '0', 'next'];

// Like the iPhone keyboard, the page stays usable under the pad; a tap outside the pad and its box closes it.
const NumberPadSheet = ({ label, value, onChange, onDone, onClose, anchorRef }) => {
  const padRef = useRef(null);
  useEffect(() => {
    const closeOnOutsideTap = (event) => {
      if (padRef.current?.contains(event.target) || anchorRef?.current?.contains(event.target)) return;
      onClose();
    };
    document.addEventListener('pointerdown', closeOnOutsideTap);
    return () => document.removeEventListener('pointerdown', closeOnOutsideTap);
  }, [anchorRef, onClose]);

  return (
    <div
      ref={padRef}
      role="dialog"
      aria-label="Number pad"
      className="fixed inset-x-0 bottom-0 z-[130] border-t border-gray-300 bg-gray-100 px-3 pb-8 pt-3 shadow-2xl"
    >
      <div className="mx-auto max-w-2xl">
        <div className="mb-3 flex items-baseline justify-between gap-3 px-1">
          <span className="min-w-0 truncate text-sm font-medium uppercase tracking-wide text-gray-500">{label}</span>
          <span aria-label="Quantity entered" className="text-3xl font-semibold text-gray-900">{value || '0'}</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {PAD_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              aria-label={key === 'back' ? 'Delete' : key === 'next' ? 'Done' : key}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => (key === 'next' ? onDone() : onChange(padKey(value || '', key)))}
              className={`h-16 rounded-xl text-2xl font-medium shadow-sm ${key === 'next' ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}
            >
              {key === 'back' ? '⌫' : key === 'next' ? 'Done' : key}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NumberPadSheet;
